import { Item } from "@/utils/encoding";
import { AnalysisResults } from "@/utils/analysis";

export interface Mode {
  key: string;
  name: string;
  check: (groups: string[]) => boolean;
  view: string;
}

export interface Token {
  index: number;
  text: string;
  span: [number, number];
  delimiter: string;
}

export interface Evidence {
  id: string;
  desc: string;
  spans: number[][];
  billable?: boolean;
  score?: number;
  pratio?: number;
  better_matches?: string[];
  lacking?: string[];
  prereqs?: {
    required: boolean;
    codes: string[];
  };
  type: "suggested" | "verified" | "custom";
}

export interface Text {
  phrase: string;
  charpos: [number, number];
  tokpos: [number, number];
}

export interface Concept {
  conceptid?: string;
  conceptname: string;
  concepttype: string;
  text: Text;
}

export interface Match {
  concept: Concept;
  context: { [key: string]: string };
  refers: { [key: string]: Match };
  relationships?: { [key: string]: Match[] };
  labels?: string[];
  score: number;
}

export interface Indexed<T> {
  index: number;
  value: T;
}

interface HasKey<T> {
  id: T;
}

interface HasDescription {
  desc: string;
}

interface HasType {
  type: string;
}

interface SupportsInheritance<T> {
  inherits?: T[];
}

export interface Entity<T>
  extends HasKey<string>,
    HasDescription,
    HasType,
    SupportsInheritance<Entity<any>> {
  attributes: {
    codeAlso?: T[];
    codeFirst?: T[];
    excludes1?: T[];
    excludes2?: T[];
    includes?: T[];
    inclusionTerm?: T[];
    notes?: T[];
    sevenChrNote?: T[];
    useAdditionalCode?: T[];
    [key: string]: any;
  };
}

export class Repository<T extends HasKey<string>> {
  private registry: { [type: string]: { [id: string]: T } } = {};

  constructor() {}

  add<K extends T>(type: string, value: K) {
    if (!(type in this.registry)) this.registry[type] = {};
    this.registry[type][value.id] = value;
  }

  get(type: string, id: string): T | undefined {
    return (this.registry[type] || {})[id];
  }

  all(type: string): T[] {
    return Object.values(this.registry[type] || {});
  }
}

export interface Code extends Entity<string> {
  index: number;
  level: number;
  tags: string[];
  billable: boolean;
}

export interface Record {
  name: string;
  date: Date;
  raw: Match;
  context: { [key: string]: string };
}

export interface Page {
  number: number;
  text: string;
  included: boolean | null;
}
